import {
  HideImage,
  InfoOutlined,
  Star,
  ShoppingCart,
  CheckCircle,
} from "@mui/icons-material";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { format, isBefore } from "date-fns";
import { sortBy, uniq } from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import {
  ENTERPRISE_FEATURE_MESSAGE,
  MEASURE_TYPES,
  SANDBOX_MODE_MESSAGE,
} from "../../utils/constants";
import { get, isOrgEnterprise, isSandbox, post } from "../../utils/io";
import theme from "../../utils/theme";
import MainContentContainer from "../Layouts/MainContentContainer";
import CartOverlay from "./CartOverlay";
import ChoicesModal from "./ChoicesModal";
import MessageModal from "./MessageModal";

export default function DeviceOrders() {
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [listDevices, setListDevices] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [makeFilter, setMakeFilter] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState({});
  const [cartQuantities, setCartQuantities] = useState({});
  const [showCart, setShowCart] = useState(false);
  const [modalName, setModalName] = useState("");
  const [loadingRequestOrder, setLoadingRequestOrder] = useState(false);
  const [inputAdditionalInformation, setInputAdditionalInformation] =
    useState("");

  const [MEASURE_DEVICE_TYPES] = useState(
    Object.keys(MEASURE_TYPES).reduce(
      (result, measureType) => ({
        ...result,
        [measureType]:
          MEASURE_TYPES[measureType].deviceType ||
          MEASURE_TYPES[measureType].label,
      }),
      {}
    )
  );

  const isPriceAndExpirationValid = (price, expiration) =>
    price && expiration && isBefore(new Date(), new Date(expiration));

  const handleSelectDevice = (deviceId) => {
    setSelectedDevices((prev) => {
      const newSelected = { ...prev };
      if (newSelected[deviceId]) {
        delete newSelected[deviceId];
        setCartQuantities((prevCart) => {
          const newCart = { ...prevCart };
          delete newCart[deviceId];
          return newCart;
        });
      } else {
        newSelected[deviceId] = true;
        setCartQuantities((prevCart) => ({
          ...prevCart,
          [deviceId]: 1,
        }));
      }
      return newSelected;
    });
  };

  const getSelectedCount = () => Object.keys(selectedDevices).length;

  const handleRemoveDevice = (deviceId) => {
    setSelectedDevices((prev) => {
      const newSelected = { ...prev };
      delete newSelected[deviceId];
      return newSelected;
    });
    setCartQuantities((prev) => {
      const newCart = { ...prev };
      delete newCart[deviceId];
      return newCart;
    });
  };

  useEffect(() => {
    const getDeviceModels = async () => {
      setLoading(true);
      const res = await get("/device_models", { include_image_url: true });
      if (res.status !== 200) {
        enqueueSnackbar({
          message: "Failed to retrieve device models",
          variant: "error",
        });
        setLoading(false);
        return;
      }
      const deviceModels = res.data.device_models.map((model) => ({
        ...model,
        deviceType: MEASURE_DEVICE_TYPES[model.type],
        missingValidPrice: !isPriceAndExpirationValid(
          model.price,
          model.price_expiration_date
        ),
      }));

      const sortedDevices = sortBy(deviceModels, [
        "missingValidPrice",
        "deviceType",
        "make",
        "display_name",
      ]);
      setDevices(sortedDevices);
      setListDevices(sortedDevices);
      setLoading(false);
    };

    getDeviceModels();
  }, [MEASURE_DEVICE_TYPES]);

  useEffect(() => {
    setListDevices(
      devices.filter(
        ({ deviceType, make }) =>
          (typeFilter.length === 0 || typeFilter.includes(deviceType)) &&
          (makeFilter.length === 0 || makeFilter.includes(make))
      )
    );
  }, [typeFilter, makeFilter, devices]);

  const onPlaceOrder = () => {
    if (!isOrgEnterprise()) {
      setModalName("enterprise_feature");
      return;
    }
    if (isSandbox()) {
      setModalName("enterprise_in_sandbox");
      return;
    }

    const badQuantities = Object.values(cartQuantities).filter((q) => q < 1);
    if (badQuantities.length > 0) {
      enqueueSnackbar({
        message: "Order quantities must be at least 1",
        variant: "error",
      });
      return;
    }
    setModalName("device_order_requests");
  };

  const onRequestDeviceOrder = async () => {
    setLoadingRequestOrder(true);
    const payload = Object.keys(cartQuantities).map((modelId) => {
      const device = devices.find(({ id }) => id.toString() === modelId);
      return {
        model_id: modelId,
        quoted_price: device.price || 0,
        quantity: cartQuantities[modelId],
      };
    });
    const res = await post("/device_order_request", {
      devices: payload,
      additional_information: inputAdditionalInformation,
    });
    if (res.status === 201) {
      setModalName("device_order_requests_success");
      setSelectedDevices({});
      setCartQuantities({});
      setShowCart(false);
    } else {
      enqueueSnackbar({
        message: "Request failed, please contact support@stel.life",
        variant: "error",
      });
      setModalName("");
    }
    setLoadingRequestOrder(false);
  };

  const getDeviceTypes = () =>
    sortBy(uniq(devices.map((device) => device.deviceType)));

  return (
    <MainContentContainer title="Device Orders" isEnterpriseOnly>
      <Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            marginBottom: "16px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 10,
            paddingTop: "10px",
            paddingBottom: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
            }}
          >
            <TextField
              name="deviceType"
              label="Type"
              select
              SelectProps={{ multiple: true }}
              variant="outlined"
              size="small"
              sx={{
                width: 200,
                marginRight: "16px",
              }}
              value={typeFilter}
              onChange={({ target: { value } }) => setTypeFilter(value)}
            >
              {getDeviceTypes().map((deviceType) => (
                <MenuItem value={deviceType} key={deviceType}>
                  {deviceType}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              limitTags={2}
              value={makeFilter}
              multiple
              disableCloseOnSelect
              size="small"
              sx={{
                width: "350px",
                marginRight: "16px",
              }}
              options={sortBy(
                uniq(
                  devices.map(({ make }) => make),
                  (make) => make.toLowerCase()
                )
              )}
              renderInput={(params) => (
                <TextField {...params} name="make" label="Manufacturer" />
              )}
              onChange={(_, value) => setMakeFilter(value)}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexGrow: 1,
              alignItems: "flex-start",
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                setTypeFilter([]);
                setMakeFilter([]);
              }}
              color="error"
            >
              Clear
            </Button>
            <Button
              variant="contained"
              sx={{
                marginLeft: "auto",
              }}
              onClick={() => setShowCart(true)}
              disabled={getSelectedCount() === 0}
              startIcon={
                <Badge badgeContent={getSelectedCount()} color="error">
                  <ShoppingCart />
                </Badge>
              }
            >
              View Cart
            </Button>
          </Box>
        </Box>
        <Grid
          container
          spacing={2}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-start",
          }}
        >
          {loading &&
            new Array(8).fill(0).map(() => (
              <Grid size={{ xs: 12, md: 4, sm: 6, lg: 3 }}>
                <DeviceSkeleton />
              </Grid>
            ))}
          {listDevices.map((device) => (
            <Grid size={{ xs: 12, md: 4, sm: 6, lg: 3 }}>
              <Card
                sx={{ height: "341px", position: "relative" }}
                key={device.id}
              >
                <CardContent sx={{ height: 53 }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Box>
                      {device.properties?.stel_detail_url ? (
                        <Link
                          href={device.properties.stel_detail_url}
                          target="_blank"
                          rel="noopener"
                          underline="hover"
                          sx={{
                            color: theme.colors.black,
                            "&:hover": { color: theme.colors.stelBlue },
                            margin: 0,
                            fontSize: "16px",
                            fontWeight: "bold",
                          }}
                        >
                          {device.make} {device.display_name}
                        </Link>
                      ) : (
                        <Box
                          sx={{
                            margin: 0,
                            fontSize: "16px",
                            fontWeight: "bold",
                          }}
                        >
                          {device.make} {device.display_name}
                        </Box>
                      )}
                      <Box sx={{ margin: 0, fontSize: "14px", color: "grey" }}>
                        {MEASURE_DEVICE_TYPES[device.type] || "N/A"}
                      </Box>
                    </Box>
                    <Stack direction="row">
                      {device.properties?.preferred && (
                        <Box>
                          <Tooltip
                            title="Popular device in the Stel ecosystem"
                            arrow
                          >
                            <Star
                              sx={{
                                fontSize: "18px",
                                color: theme.colors.stelBlue,
                              }}
                            />
                          </Tooltip>
                        </Box>
                      )}
                      {device.properties?.stel_detail_url && (
                        <Box>
                          <IconButton
                            href={device.properties.stel_detail_url}
                            target="_blank"
                            rel="noopener"
                            edge="end"
                            disableRipple
                            sx={{
                              color: theme.colors.black,
                              "&:hover": { color: theme.colors.stelBlue },
                              top: "-8px",
                            }}
                          >
                            <InfoOutlined
                              sx={{
                                fontSize: "18px",
                              }}
                            />
                          </IconButton>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </CardContent>
                {device.image?.url && device.properties?.stel_detail_url && (
                  <Link
                    href={device.properties.stel_detail_url}
                    sx={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                    target="_blank"
                    rel="noopener"
                  >
                    <CardMedia
                      component="img"
                      image={device.image.url}
                      sx={{
                        width: "inherit",
                        padding: "0 16px",
                        height: "150px",
                        margin: "auto",
                      }}
                    />
                  </Link>
                )}
                {device.image?.url && !device.properties?.stel_detail_url && (
                  <CardMedia
                    component="img"
                    image={device.image.url}
                    sx={{
                      width: "inherit",
                      padding: "0 16px",
                      height: "150px",
                      margin: "auto",
                    }}
                  />
                )}
                {!device.image?.url && (
                  <HideImage
                    sx={{
                      width: "100%",
                      height: "150px",
                      color: grey[400],
                    }}
                  />
                )}
                <CardContent
                  sx={{ position: "absolute", bottom: 0, right: 0, left: 0 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          margin: 0,
                          fontSize: "14px",
                        }}
                      >
                        Enterprise Price
                      </Typography>
                      <Typography sx={{ margin: 0 }}>
                        {device.price ? `$${device.price}` : "-"}
                      </Typography>
                      {isPriceAndExpirationValid(
                        device.price,
                        device.price_expiration_date
                      ) && (
                        <Typography
                          sx={{
                            color: "grey",
                            margin: 0,
                            fontSize: "14px",
                            fontStyle: "italic",
                          }}
                        >
                          Valid Through{" "}
                          {format(
                            new Date(device.price_expiration_date),
                            "MM/dd/yy"
                          )}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ marginTop: "auto" }}>
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: "capitalize",
                          backgroundColor: selectedDevices[device.id]
                            ? "#fff"
                            : theme.colors.stelBlue,
                          color: selectedDevices[device.id]
                            ? theme.colors.stelBlue
                            : "#fff",
                          border: `1px solid ${theme.colors.stelBlue}`,
                          "&:hover": {
                            backgroundColor: selectedDevices[device.id]
                              ? "#f0f0f0"
                              : "#1F79E2",
                          },
                        }}
                        onClick={() => handleSelectDevice(device.id)}
                        startIcon={
                          selectedDevices[device.id] ? <CheckCircle /> : null
                        }
                      >
                        {selectedDevices[device.id] ? "Selected" : "Select"}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
      {modalName === "device_order_requests_success" && (
        <MessageModal
          title="Device Order Request Received"
          isDisplayed
          onClose={() => {
            setModalName("");
          }}
          message="Thank you for submitting your request. A representative from Stel will contact you with more information."
        />
      )}
      {modalName === "device_order_requests" && (
        <ChoicesModal
          isDisplayed
          title="Device Order Request"
          onConfirm={onRequestDeviceOrder}
          onClose={() => setModalName("")}
          quantities={cartQuantities}
          devices={devices}
          inputAdditionalInformation={inputAdditionalInformation}
          setInputAdditionalInformation={setInputAdditionalInformation}
          loadingRequestOrder={loadingRequestOrder}
        />
      )}
      {modalName === "enterprise_feature" && (
        <MessageModal
          title="Enterprise Feature"
          isDisplayed
          onClose={() => {
            setModalName("");
          }}
          message={ENTERPRISE_FEATURE_MESSAGE}
        />
      )}
      {modalName === "enterprise_in_sandbox" && (
        <MessageModal
          title="Switch to Production Mode"
          isDisplayed
          onClose={() => {
            setModalName("");
          }}
          message={SANDBOX_MODE_MESSAGE}
        />
      )}
      <CartOverlay
        isOpen={showCart}
        onClose={() => setShowCart(false)}
        selectedDevices={selectedDevices}
        cartQuantities={cartQuantities}
        setCartQuantities={setCartQuantities}
        devices={devices}
        onPlaceOrder={onPlaceOrder}
        onRemoveDevice={handleRemoveDevice}
      />
    </MainContentContainer>
  );
}

function DeviceSkeleton() {
  return (
    <Card>
      <Box sx={{ padding: "16px" }}>
        <Skeleton variant="text" width={170} height={28} />
        <Skeleton variant="text" width={100} height={20} />
      </Box>
      <Skeleton
        variant="rectangular"
        height={200}
        sx={{
          margin: "0 16px",
          borderRadius: "4px",
        }}
      />
      <CardContent>
        <Skeleton variant="text" width={140} height={20} />
        <Skeleton variant="text" width={60} height={20} />
        <Skeleton variant="text" width={200} height={20} />
      </CardContent>
    </Card>
  );
}
